const { useEffect, useMemo, useRef, useState } = require('react');
const { context } = require('app-context');
const { v4: uuid } = require('uuid');
const { previewViewer } = require('./useNerpy');

const internals = {};

exports.usePeerGames = ({ user, sid, enabled }) => {

    const eligible = enabled && previewViewer(user, user?.schoolId) && /^CH[a-f0-9]{32}$/i.test(sid || '');
    const scope = `${user?.schoolId}:${user?.roleId}:${user?.id}:${sid}`;
    const current = useRef(scope);
    current.current = scope;
    const mounted = useRef(false);
    const epoch = useRef(0);
    const state = useRef(null);
    const pending = useRef(null);
    const inFlight = useRef(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState(null);
    const api = context.api?.nearpeer?.nerpy;

    const accept = (value, generation = epoch.current, older = false) => {

        if (!mounted.current || current.current !== scope || generation !== epoch.current) { return; }
        const old = state.current;
        const events = [...new Map([...(old?.events || []), ...(value.events || [])].map((e) => [e.id, e])).values()].sort((a, b) => a.id - b.id);
        const newer = !old?.session || !value.session || value.session.id > old.session.id || (value.session.id === old.session.id && value.session.version >= old.session.version);
        const next = { ...value, session: newer ? value.session : old.session, events,
            before: older || !old ? value.before : old.before };
        state.current = next;
        setData({ scope, ...next });
        if (old && newer && value.session && (old.session?.id !== value.session.id || old.session?.version !== value.session.version)) {
            window.dispatchEvent(new Event('gamification-progress'));
        }
    };

    const fail = (failure, message, generation = epoch.current) => {

        if (!mounted.current || current.current !== scope || generation !== epoch.current) { return; }
        const status = failure?.response?.status || failure?.statusCode;
        if ([401, 403, 404].includes(status)) {
            ++epoch.current;
            state.current = { enabled: false, events: [] };
            pending.current = null;
            inFlight.current = false;
            setBusy(false);
            setData({ scope, enabled: false, events: [] });
            setError('');
        }
        else { setError(message); }
    };

    const refresh = async (before) => {

        const generation = epoch.current;
        try {
            accept(await api.peerGames(sid, before), generation, !!before);
            if (!pending.current && mounted.current && current.current === scope && generation === epoch.current) { setError(''); }
        }
        catch (failure) {
            fail(failure, 'Game updates are unavailable. Your conversation still works.', generation);
        }
    };

    useEffect(() => {

        mounted.current = true;
        ++epoch.current;
        state.current = null;
        pending.current = null;
        inFlight.current = false;
        setData(null);
        setBusy(false);
        setError('');
        if (!eligible || !api?.peerGames) { return () => { mounted.current = false; }; }

        let polling = false;
        const update = async () => {

            if (polling || document.visibilityState === 'hidden' || state.current?.enabled === false) { return; }
            polling = true;
            await refresh();
            polling = false;
        };
        const preferencesChanged = () => {

            ++epoch.current;
            state.current = null;
            pending.current = null;
            inFlight.current = false;
            setBusy(false);
            setData(null);
            update();
        };

        update();
        const timer = setInterval(update, 8000);
        window.addEventListener('focus', update);
        window.addEventListener('online', update);
        document.addEventListener('visibilitychange', update);
        window.addEventListener('nerpy-preferences-changed', preferencesChanged);
        window.addEventListener('gamification-participation', preferencesChanged);
        return () => {

            mounted.current = false;
            clearInterval(timer);
            window.removeEventListener('focus', update);
            window.removeEventListener('online', update);
            document.removeEventListener('visibilitychange', update);
            window.removeEventListener('nerpy-preferences-changed', preferencesChanged);
            window.removeEventListener('gamification-participation', preferencesChanged);
        };
    }, [scope, eligible]);

    const perform = async (request) => {

        if (inFlight.current || current.current !== scope) { return; }
        inFlight.current = true;
        const generation = epoch.current;
        pending.current = request;
        setBusy(true);
        setError('');
        try {
            const value = request.kind === 'invite' ? await api.invitePeerGame(sid, request.payload)
                : await api.peerGameAction(sid, request.sessionId, request.payload);
            if (mounted.current && current.current === scope && generation === epoch.current) {
                accept(value, generation);
                pending.current = null;
                setDialog((old) => old?.scope === scope && old.sessionId === null ? { ...old, sessionId: value.session?.id } : old);
                return value.session?.id;

            }
        }
        catch (failure) {
            if (mounted.current && current.current === scope && generation === epoch.current) {
                const status = failure?.response?.status || failure?.statusCode;
                if ([400, 409].includes(status)) {
                    pending.current = null;
                    await refresh();
                    if (current.current === scope && generation === epoch.current) {
                        setError('The game changed or that action is unavailable. Review the latest game before trying again.');
                    }
                }
                else { fail(failure, 'We could not confirm the game action. Retry to check it safely.', generation); }
            }
        }
        finally {
            if (mounted.current && current.current === scope && generation === epoch.current) {
                inFlight.current = false;
                setBusy(false);
            }
        }
    };

    const visible = eligible && data?.scope === scope ? data : null;
    const summaries = useMemo(() => {
        const grouped = new Map();
        for (const event of visible?.events || []) {
            const first = grouped.get(event.sessionId);
            grouped.set(event.sessionId, { ...event, id: event.sessionId, createdAt: first?.createdAt || event.createdAt });
        }

        return [...grouped.values()];
    }, [visible?.events]);
    return { ...visible, summaries, dialogOpen: !!visible?.enabled && dialog?.scope === scope,
        selectedSessionId: dialog?.sessionId,
        openGame: (sessionId = null) => setDialog({ scope, sessionId }),
        closeGame: () => setDialog(null),
        enabled: !!visible?.enabled, events: visible?.events || [], busy, error,
        uncertain: !!pending.current,
        refresh: () => refresh(),
        older: () => visible?.before && refresh(visible.before),
        retry: () => pending.current ? perform(pending.current) : refresh(),
        invite: (gameKey) => !pending.current && perform({ kind: 'invite', payload: { requestId: uuid(), gameKey } }),
        action: (action, guess) => !pending.current && visible?.session && perform({ kind: 'action', sessionId: visible.session.id,
            payload: { requestId: uuid(), expectedVersion: visible.session.version, action, ...(action === 'guess' ? { guess } : action === 'contribute' ? { choices: guess.choices } : action === 'propose' ? { selections: guess.selections } : {}) } }) };
};

// Pure merge helper keeps game events out of optimistic sends, receipts, and Twilio state.
exports.mergeGameMessages = (messages, events) => {

    if (!events?.length) { return messages; }
    return [...messages, ...events.map((event) => ({ sid: `nerpy-game-${event.id}`, timestamp: event.createdAt, nerpyGameEvent: event }))]
        .sort((a, b) => internals.time(a.timestamp) - internals.time(b.timestamp) || String(a.sid).localeCompare(String(b.sid)));
};

internals.time = (value) => new Date(value).getTime() || 0;
